import api from '@/api/index'
import AppQueryClient from '@/libs/appQueryClient'
import type { FilterLabel, FilterOptions } from '@/views/settings/filters/types'
import type {
  SecurityChannel,
  SecurityOptions,
  SecurityPage,
  SecurityConfigChannelOption,
} from '@/views/security/types'

type IndexOptions = {
  type:
    | 'conversation.status'
    | 'ticket.status'
    | 'conversation.priority'
    | 'rule.condition_type'
    | 'rule.attribute_key'
    | 'rule.condition_inner_type'
}

type QueryType =
  | 'index'
  | 'department'
  | 'user'
  | 'role'
  | 'survey'
  | 'ticketCategories'
  | 'issueCategories'
  | 'channel'

type QueryKeys = {
  [key in QueryType]: string[]
}
const queryKeys: QueryKeys = {
  user: ['user', 'options'],
  role: ['role', 'options'],
  index: ['index', 'options'],
  survey: ['survey', 'options'],
  department: ['department', 'options'],
  ticketCategories: ['ticket-categories', 'options'],
  issueCategories: ['issue-categories', 'options'],
  channel: ['channel', 'options'],
}

const staleTime = 1000 * 60 * 3

export const getIndexOptions = (options: IndexOptions) => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: [...queryKeys.index, options.type],
      queryFn: () => api.get('/index/options', { params: { type: options.type } }),
    })
}

export const getDepartmentOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.department,
      queryFn: () => api.get('/teams', { params: { scene: 'options' } }),
    })
}

export const getUserOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.user,
      queryFn: () =>
        api.get('/user/options').then((data) =>
          data.map((item) => ({
            value: item.id,
            label: item.username,
          })),
        ),
    })
}

export const getRoleOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.role,
      queryFn: () => api.get('/roles', { params: { scene: 'options' } }),
    })
}

export const getSurveyOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.survey,
      queryFn: () => api.get('/survey', { params: { scene: 'options' } }),
    })
}

export const getTicketCategoryOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.ticketCategories,
      queryFn: () => api.get('/ticket-categories', { params: { scene: 'options' } }),
    })
}

export const getIssueCategoryOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.issueCategories,
      queryFn: () => api.get('/issue-categories', { params: { scene: 'options' } }),
    })
}

type ChannelOption = {
  value: number
  label: string
}

// Raw fetcher shared by the ticket list dropdown and the reply-history drawer.
// A failed options request must not surface a global error toast.
export const fetchChannelOptions = () =>
  api.get<ChannelOption[]>('/channel', {
    params: { scene: 'options' },
    suppressErrorToast: true,
  })

export const getChannelOptions = () => {
  return () =>
    AppQueryClient.fetchQuery({
      staleTime,
      queryKey: queryKeys.channel,
      queryFn: fetchChannelOptions,
    })
}

export const invalidateCommonApi = (key: QueryType) =>
  AppQueryClient.invalidateQueries({ queryKey: queryKeys[key] })

export const getFilterOptions = () => () =>
  AppQueryClient.fetchQuery({
    staleTime,
    queryKey: ['settings', 'filters', 'options'],
    queryFn: () => api.get<FilterOptions>('/filters/options'),
  })

export const getFilterSearchOptions = () => () =>
  AppQueryClient.fetchQuery({
    staleTime,
    queryKey: ['tickets', 'filter-search', 'options'],
    queryFn: () => api.get<FilterOptions>('/filters/search-options'),
  })

export const getFilterLabels = () => () =>
  AppQueryClient.fetchQuery({
    staleTime: 0,
    queryKey: ['settings', 'filters', 'labels'],
    queryFn: () => api.get<FilterLabel[]>('/labels', { params: { scene: 'list' } }),
  })

export const fetchSecurityConfigChannels = (
  params: { page: number; page_size: number; channel_id?: number },
  signal?: AbortSignal,
) =>
  api.get<SecurityPage<SecurityConfigChannelOption>>('/security/configs/channels', { params, signal })

export const fetchSecurityRecordChannels = (
  params: { page: number; page_size: number },
  signal?: AbortSignal,
) =>
  api.get<SecurityPage<SecurityChannel>>('/security/records/channels', { params, signal })

export const getSecurityConfigOptions = (channelId: number) => () =>
  AppQueryClient.fetchQuery({
    staleTime: 0,
    queryKey: ['security', 'channel-rules-v1', 'config-options', channelId],
    queryFn: ({ signal }) =>
      api.get<SecurityOptions>('/security/configs/options', {
        params: { channel_id: channelId },
        signal,
      }),
  })
